<template>
  <div>
    <loading :active.sync="loading" :can-cancel="false" :is-full-page="false" />

    <div v-if="errors.length > 0">
      <CAlert v-for="(msg, i) in errors" :key="i" color="danger" closeButton>{{ msg }}</CAlert>
    </div>

    <div v-if="!hasAccess" class="empty-state">
      No tienes acceso a este módulo.
    </div>

    <div v-if="hasAccess">

      <!-- ── CONTROLES ──────────────────────────────────────── -->
      <CCard>
        <CCardBody>
          <div class="meta-header">
            <div class="meta-header-title">
              <strong>GoGuau · Meta Ads Dashboard</strong>
              <span v-if="lastRefresh" class="meta-refresh-label">Actualizado: {{ lastRefresh }}</span>
            </div>
            <div class="meta-period-row">
              <div class="meta-tabs-scroll">
                <b-button-group size="sm">
                  <b-button
                    v-for="tab in periodTabs"
                    :key="tab.value"
                    :variant="period === tab.value ? 'primary' : 'outline-secondary'"
                    @click="setPeriod(tab.value)"
                  >{{ tab.label }}</b-button>
                </b-button-group>
              </div>
              <b-button size="sm" variant="outline-secondary" class="meta-refresh-btn" @click="loadData(true)">↺ Actualizar</b-button>
            </div>
          </div>

          <div v-if="period === 'custom'" class="meta-custom-range mt-2">
            <b-form-input type="date" v-model="dateFrom" size="sm" style="width:145px" />
            <span style="color:#6b7094;padding:0 6px">—</span>
            <b-form-input type="date" v-model="dateTo" size="sm" style="width:145px" />
            <b-button size="sm" variant="primary" class="ml-2" @click="loadData(true)">Aplicar</b-button>
          </div>
        </CCardBody>
      </CCard>

      <!-- ── KPI CARDS ──────────────────────────────────────── -->
      <b-row class="mb-3">
        <b-col lg="2" sm="6" class="mb-2" v-for="kpi in kpiCards" :key="kpi.label">
          <CCard class="h-100 mb-0">
            <CCardBody class="p-3">
              <div style="font-size:.68rem;color:#6b7094;text-transform:uppercase;letter-spacing:.07em;margin-bottom:4px">{{ kpi.label }}</div>
              <div :style="kpi.color ? `font-size:1.4rem;font-weight:800;color:${kpi.color}` : 'font-size:1.4rem;font-weight:800'">{{ kpi.value }}</div>
              <div style="font-size:.72rem;color:#6b7094;margin-top:4px">{{ kpi.sub }}</div>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

      <!-- ── KPIs CALCULADOS ───────────────────────────────────── -->
      <b-row class="mb-3">
        <b-col lg="3" sm="6" class="mb-2" v-for="kpi in calculatedKpis" :key="kpi.label">
          <CCard class="h-100 mb-0">
            <CCardBody class="p-3">
              <div style="font-size:.68rem;color:#6b7094;text-transform:uppercase;letter-spacing:.07em;margin-bottom:4px">{{ kpi.label }}</div>
              <div :style="kpi.color ? `font-size:1.4rem;font-weight:800;color:${kpi.color}` : 'font-size:1.4rem;font-weight:800'">{{ kpi.value }}</div>
              <div style="font-size:.72rem;color:#6b7094;margin-top:4px">{{ kpi.sub }}</div>
            </CCardBody>
          </CCard>
        </b-col>
        <b-col lg="12" class="mb-2">
          <div style="font-size:.7rem;color:#6b7094">
            Ticket promedio (editable):
            <b-form-input v-model.number="avgTicket" type="number" size="sm" style="width:130px;display:inline-block;margin-left:6px" />
            <span style="margin-left:4px">COP/servicio</span>
          </div>
        </b-col>
      </b-row>

      <!-- ── ALERTAS ─────────────────────────────────────────── -->
      <h6 class="section-title">Alertas y recomendaciones</h6>
      <CCard class="mb-3">
        <CCardBody>
          <div v-if="alerts.length === 0" class="text-muted" style="font-size:.85rem">Cargando análisis...</div>
          <div v-for="(alert, i) in alerts" :key="i" :class="['alert-item', alert.type]" class="mb-2 p-3 rounded">
            <div style="font-weight:700;font-size:.85rem">{{ alert.icon }} {{ alert.title }}</div>
            <div style="font-size:.78rem;color:#6b7094;margin-top:3px">{{ alert.body }}</div>
            <div v-if="alert.action" :class="`alert-action-${alert.type}`" style="font-size:.73rem;font-weight:700;margin-top:5px">{{ alert.action }}</div>
          </div>
        </CCardBody>
      </CCard>

      <!-- ── TABLA DE CAMPAÑAS ──────────────────────────────── -->
      <h6 class="section-title">Rendimiento por campaña — haz clic en una fila para ver sus anuncios</h6>
      <CCard class="mb-3">
        <CCardBody>
          <div class="table-responsive">
            <table class="table table-sm" style="font-size:.82rem">
              <thead>
                <tr>
                  <th style="width:28px"></th>
                  <th @click="sortTable('name')" style="cursor:pointer">Campaña ↕</th>
                  <th>Estado</th>
                  <th @click="sortTable('spend')" style="cursor:pointer"
                    v-b-tooltip.hover title="Presupuesto consumido en el período seleccionado (COP).">
                    Gasto ↕</th>
                  <th @click="sortTable('convs')" style="cursor:pointer"
                    v-b-tooltip.hover title="Conversaciones de WhatsApp iniciadas gracias al anuncio. Es el principal KPI de GoGuau.">
                    Conv. ↕</th>
                  <th @click="sortTable('cpr')" style="cursor:pointer"
                    v-b-tooltip.hover title="Costo Por Resultado — cuánto cuesta en COP conseguir 1 conversación (Gasto ÷ Conv.). Verde ≤ $2.5K · Amarillo ≤ $4K · Rojo > $4K. Benchmark histórico: $3.518.">
                    CPR ↕</th>
                  <th v-b-tooltip.hover title="Diferencia % entre el CPR de esta campaña y el CPR promedio de todas las campañas. ▲ = más caro que el promedio (malo) · ▼ = más barato (bueno).">
                    vs promedio</th>
                  <th @click="sortTable('reach')" style="cursor:pointer"
                    v-b-tooltip.hover title="Personas únicas que vieron el anuncio al menos 1 vez en el período.">
                    Alcance ↕</th>
                  <th v-b-tooltip.hover title="Recomendación del algoritmo basada en CPR, frecuencia y CTR. Haz clic en la campaña para ver sus anuncios.">
                    Acción</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="tableRows.length === 0">
                  <td colspan="9" class="text-center text-muted py-4">No hay datos para el período seleccionado</td>
                </tr>

                <template v-for="row in tableRows">
                  <!-- Fila de campaña -->
                  <tr :key="'c-' + row.id" class="campaign-row" @click="toggleCampaign(row.id)" style="cursor:pointer">
                    <td class="text-center" style="color:#6b7094;font-size:.8rem">
                      {{ expandedCampaigns[row.id] ? '▾' : '▸' }}
                    </td>
                    <td>
                      <div style="max-width:220px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;font-weight:600" :title="row.name">{{ row.name }}</div>
                      <div style="font-size:.7rem;color:#6b7094">{{ row.hint }}</div>
                    </td>
                    <td>
                      <b-badge :variant="row.status === 'ACTIVE' ? 'success' : 'secondary'">
                        {{ row.status === 'ACTIVE' ? '● Activa' : '◌ Pausada' }}
                      </b-badge>
                    </td>
                    <td>{{ copFmt(row.spend) }}</td>
                    <td>{{ numFmt(row.convs) }}</td>
                    <td>
                      <span :style="`font-weight:700;color:${cprColor(row.cpr)}`">
                        {{ row.cpr > 0 ? copFmt(row.cpr) : '—' }}
                      </span>
                    </td>
                    <td>
                      <span v-if="row.delta !== null" :style="`color:${row.delta > 0 ? '#ef4444' : '#22c55e'}`">
                        {{ row.delta > 0 ? '▲' : '▼' }}{{ Math.abs(row.delta) }}%
                      </span>
                      <span v-else>—</span>
                    </td>
                    <td>{{ numFmt(row.reach) }}</td>
                    <td>
                      <b-badge :variant="recVariant(row.rec.cls)" :title="row.rec.reason">{{ row.rec.label }}</b-badge>
                    </td>
                  </tr>

                  <!-- Filas de anuncios (expandibles) -->
                  <template v-if="expandedCampaigns[row.id]">
                    <tr v-if="!adsByCampaign[row.id] || adsByCampaign[row.id].length === 0" :key="'ae-' + row.id">
                      <td colspan="9" class="ad-row text-muted" style="font-size:.78rem;padding-left:40px">
                        Sin datos de anuncios para este período
                      </td>
                    </tr>
                    <!-- Cabecera de anuncios -->
                    <tr :key="'ah-' + row.id" class="ad-header-row">
                      <td></td>
                      <td class="ad-th">Anuncio</td>
                      <td class="ad-th">Estado</td>
                      <td class="ad-th" v-b-tooltip.hover title="Presupuesto consumido por este anuncio en el período (COP).">Gasto</td>
                      <td class="ad-th" v-b-tooltip.hover title="Conversaciones de WhatsApp iniciadas directamente por este anuncio.">Conv.</td>
                      <td class="ad-th" v-b-tooltip.hover title="Costo Por Resultado a nivel de anuncio individual (Gasto ÷ Conv.). Verde ≤ $2.5K · Amarillo ≤ $4K · Rojo > $4K.">CPR</td>
                      <td class="ad-th" v-b-tooltip.hover title="Click-Through Rate — % de personas que hicieron clic en el anuncio. ≥1.5% excelente · 0.8–1.5% aceptable · <0.8% bajo (la imagen o el copy no conectan).">CTR</td>
                      <td class="ad-th" v-b-tooltip.hover title="Frecuencia — promedio de veces que cada persona vio este anuncio. <2.5x saludable · 2.5–3.5x vigilar · >3.5x audiencia saturada, urge rotar el creativo.">Frec.</td>
                      <td class="ad-th" v-b-tooltip.hover title="Recomendación del algoritmo con explicación detallada de la razón. Los anuncios verdes (ESCALAR/AMPLIAR) y amarillos (OPTIMIZAR) tienen botón para generar creativos nuevos.">Acción · Por qué</td>
                    </tr>
                    <tr v-for="ad in adsForCampaign(row.id)" :key="'a-' + ad.adId" class="ad-row">
                      <td></td>
                      <td style="padding-left:32px">
                        <div style="max-width:200px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap" :title="ad.name">{{ ad.name }}</div>
                      </td>
                      <td>—</td>
                      <td>{{ copFmt(ad.spend) }}</td>
                      <td>{{ numFmt(ad.convs) }}</td>
                      <td>
                        <span :style="`font-weight:700;color:${cprColor(ad.cpr)}`">
                          {{ ad.cpr > 0 ? copFmt(ad.cpr) : '—' }}
                        </span>
                      </td>
                      <td :style="`color:${ad.ctr >= 1.5 ? '#22c55e' : ad.ctr >= 0.8 ? '#f59e0b' : '#ef4444'}`">
                        {{ ad.ctr > 0 ? ad.ctr.toFixed(2) + '%' : '—' }}
                      </td>
                      <td :style="`color:${ad.freq < 2.5 ? '#22c55e' : ad.freq < 3.5 ? '#f59e0b' : '#ef4444'}`">
                        {{ ad.freq > 0 ? ad.freq.toFixed(1) + 'x' : '—' }}
                      </td>
                      <td>
                        <b-badge :variant="recVariant(ad.rec.cls)" class="mb-1">{{ ad.rec.label }}</b-badge>
                        <div style="font-size:.68rem;color:#6b7094;line-height:1.4;max-width:280px;white-space:normal">{{ ad.rec.why }}</div>
                        <b-button v-if="ad.rec.cls === 'green' || ad.rec.cls === 'yellow'" size="sm" :variant="ad.rec.cls === 'green' ? 'outline-success' : 'outline-warning'" class="mt-1" style="font-size:.68rem" @click.stop="openCreativeModal(ad, row)">
                          ✦ Generar creativo
                        </b-button>
                      </td>
                    </tr>
                  </template>
                </template>
              </tbody>
            </table>
          </div>
        </CCardBody>
      </CCard>

      <!-- ── GRÁFICAS ────────────────────────────────────────── -->
      <h6 class="section-title">Tendencia diaria</h6>
      <CCard class="mb-3">
        <CCardBody>
          <div style="font-size:.82rem;color:#6b7094;margin-bottom:10px">Gasto diario por campaña (COP)</div>
          <CChartLine v-if="dailySpendChart.datasets.length" :datasets="dailySpendChart.datasets" :labels="dailySpendChart.labels" :options="lineChartOptions(true)" style="height:240px" />
          <div v-else class="text-muted text-center py-4" style="font-size:.85rem">Sin datos diarios</div>
        </CCardBody>
      </CCard>

      <b-row>
        <b-col lg="6">
          <CCard class="mb-3">
            <CCardBody>
              <div style="font-size:.82rem;color:#6b7094;margin-bottom:10px">CPR diario (COP/conversación)</div>
              <CChartLine v-if="dailyCprChart.datasets.length" :datasets="dailyCprChart.datasets" :labels="dailyCprChart.labels" :options="lineChartOptions(true)" style="height:220px" />
              <div v-else class="text-muted text-center py-4" style="font-size:.85rem">Sin datos</div>
            </CCardBody>
          </CCard>
        </b-col>
        <b-col lg="6">
          <CCard class="mb-3">
            <CCardBody>
              <div style="font-size:.82rem;color:#6b7094;margin-bottom:10px">Conversaciones por campaña</div>
              <CChartBar v-if="campaignConvsChart.datasets.length" :datasets="campaignConvsChart.datasets" :labels="campaignConvsChart.labels" :options="barHorizOptions()" style="height:220px" />
              <div v-else class="text-muted text-center py-4" style="font-size:.85rem">Sin datos</div>
            </CCardBody>
          </CCard>
        </b-col>
      </b-row>

      <!-- ── PLACEMENT ──────────────────────────────────────── -->
      <h6 class="section-title">Origen / Plataforma</h6>
      <CCard class="mb-3">
        <CCardBody>
          <b-row>
            <b-col lg="5">
              <CChartDoughnut v-if="placementChart.datasets.length" :datasets="placementChart.datasets" :labels="placementChart.labels" :options="doughnutOptions()" style="height:220px" />
              <div v-else class="text-muted text-center py-4" style="font-size:.85rem">Sin datos de placement</div>
            </b-col>
            <b-col lg="7">
              <b-row>
                <b-col sm="6" v-for="(pl, i) in placementBreakdown" :key="i" class="mb-2">
                  <div class="p-2 rounded" style="background:#1c2035;border:1px solid #252840">
                    <div style="font-size:.7rem;color:#6b7094">{{ pl.label }}</div>
                    <div style="font-size:1.1rem;font-weight:700" :style="`color:${COLORS[i]}`">{{ copFmt(pl.spend) }}</div>
                    <div style="font-size:.7rem;color:#6b7094">{{ pl.pct }}% del gasto</div>
                    <div style="font-size:.7rem;color:#6b7094">{{ numFmt(pl.convs) }} conv · {{ pl.cpr ? copFmt(pl.cpr)+'/conv' : '—' }}</div>
                  </div>
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </CCardBody>
      </CCard>

      <!-- ── DESGLOSE MENSUAL ───────────────────────────────── -->
      <h6 class="section-title">Desglose mensual (últimos 12 meses)</h6>
      <CCard class="mb-3">
        <CCardBody>
          <b-row class="mb-3">
            <b-col sm="3" v-for="(m, i) in monthlyBreakdown" :key="i" class="mb-2">
              <div class="p-2 rounded" style="background:#1c2035;border:1px solid #252840">
                <div style="font-size:.7rem;color:#6b7094">{{ m.label }}</div>
                <div style="font-size:1rem;font-weight:700" :style="`color:${cprColor(m.cpr)}`">{{ copFmt(m.spend) }}</div>
                <div style="font-size:.72rem;color:#6b7094">{{ m.convs }} conv · {{ copFmt(m.cpr) }}/conv</div>
              </div>
            </b-col>
          </b-row>
          <CChartBar v-if="monthlyChart.datasets.length" :datasets="monthlyChart.datasets" :labels="monthlyChart.labels" :options="monthlyChartOptions()" style="height:200px" />
        </CCardBody>
      </CCard>

      <!-- ── TIMELINE ──────────────────────────────────────────── -->
      <MetaTimeline ref="timeline" />

      <!-- ── MODAL GENERADOR DE CREATIVOS ─────────────────────── -->
      <b-modal v-model="creative.show" size="xl" title="✦ Generador de Creativos — GoGuau + Andromeda 2026" hide-footer>
        <div v-if="creative.ad">

          <!-- Anuncio base -->
          <div class="p-2 mb-3 rounded" style="background:#f0f4ff;border:1px solid #c7d2fe;font-size:.82rem">
            <strong>Anuncio base:</strong> {{ creative.ad.name }}
            <span class="mx-2" style="color:#6b7094">·</span>CPR {{ copFmt(creative.ad.cpr) }}
            <span class="mx-2" style="color:#6b7094">·</span>CTR {{ creative.ad.ctr.toFixed(2) }}%
            <span class="mx-2" style="color:#6b7094">·</span>{{ creative.ad.convs }} conv.
          </div>

          <!-- PASO 1 -->
          <div class="step-block mb-3">
            <div class="step-header">
              <span class="step-badge">1</span>
              <strong>Configurar el creativo</strong>
            </div>

            <b-form-group label="Ángulo Andromeda 2026:" label-class="font-weight-bold mb-1" class="mb-3">
              <div class="angle-grid">
                <div
                  v-for="(angle, i) in ANDROMEDA_ANGLES"
                  :key="i"
                  :class="['angle-card', creative.angleIdx === i ? 'selected' : '']"
                  @click="creative.angleIdx = i"
                >
                  <div style="font-size:1.3rem;line-height:1">{{ angle.icon }}</div>
                  <div style="font-size:.75rem;font-weight:700;margin-top:4px">{{ angle.name }}</div>
                  <div style="font-size:.65rem;color:#6b7094;line-height:1.3;margin-top:2px">{{ angle.format }}</div>
                </div>
              </div>
              <div class="mt-2 p-2 rounded" style="background:#fff3cd;border:1px solid #ffc107;font-size:.75rem;color:#856404">
                <strong>Ejemplo GoGuau:</strong> "{{ ANDROMEDA_ANGLES[creative.angleIdx].goguau }}"<br>
                <span style="color:#6b7094">{{ ANDROMEDA_ANGLES[creative.angleIdx].description }}</span>
              </div>
            </b-form-group>

            <b-form-group label="Principio psicológico:" label-class="font-weight-bold mb-1" class="mb-3">
              <b-form-select v-model="creative.principleIdx" :options="PRINCIPLES.map((p,i) => ({value:i, text:p.name}))" size="sm" />
              <div class="mt-1 p-2 rounded" style="background:#e8f5e9;font-size:.72rem;color:#2e7d32">
                {{ PRINCIPLES[creative.principleIdx].description }}
              </div>
            </b-form-group>

            <b-form-group label="Comentario / ajuste del gestor:" label-class="font-weight-bold mb-1" class="mb-3">
              <b-form-textarea
                v-model="creative.comment"
                rows="2"
                size="sm"
                placeholder="Ej: enfatizar GPS en tiempo real, evitar mencionar precios, público son mamás con trabajo de oficina…"
              />
            </b-form-group>

            <b-button variant="primary" :disabled="creative.claudeGenerating" @click="generateCopy">
              <b-spinner v-if="creative.claudeGenerating" small class="mr-1" />
              {{ creative.claudeGenerating ? 'Analizando con Claude…' : '→ Generar Copy con Claude (gratis)' }}
            </b-button>
          </div>

          <!-- Loading Claude -->
          <div v-if="creative.claudeGenerating" class="text-center py-4">
            <b-spinner variant="primary" style="width:2.5rem;height:2.5rem" />
            <div class="mt-2 font-weight-bold" style="color:#3b82f6">Analizando con Claude…</div>
            <div style="font-size:.78rem;color:#6b7094">Segundos, no minutos.</div>
          </div>

          <CAlert v-if="creative.error" color="danger" class="mt-2">{{ creative.error }}</CAlert>

          <!-- PASO 2 + 3 -->
          <div v-if="creative.copyReady && !creative.claudeGenerating" class="step-block mb-3">
            <div class="step-header mb-3">
              <span class="step-badge step-badge-2">2</span>
              <strong>Revisar y editar el copy</strong>
              <span style="font-size:.72rem;color:#6b7094;margin-left:8px">Todos los campos son editables</span>
            </div>

            <b-row>
              <b-col md="6">
                <div class="creative-field mb-2">
                  <div class="creative-field-label">TITULAR <span style="font-weight:400;color:#6b7094">({{ (creative.result.titular||'').length }}/40)</span></div>
                  <b-form-input v-model="creative.result.titular" size="sm" maxlength="60" />
                </div>
                <div class="creative-field mb-2">
                  <div class="creative-field-label">TEXTO PRINCIPAL <span style="font-weight:400;color:#6b7094">({{ (creative.result.texto_principal||'').length }}/125)</span></div>
                  <b-form-textarea v-model="creative.result.texto_principal" rows="3" size="sm" />
                </div>
                <div class="creative-field mb-2">
                  <div class="creative-field-label">BOTÓN CTA</div>
                  <b-form-input v-model="creative.result.cta" size="sm" maxlength="25" />
                </div>
                <div v-if="creative.result.por_que" class="mt-2 p-2 rounded" style="background:#e8f5e9;font-size:.73rem;color:#2e7d32">
                  <strong>¿Por qué funciona?</strong> {{ creative.result.por_que }}
                </div>
                <div v-if="creative.result.segmentacion" class="mt-2 p-2 rounded" style="background:#e3f2fd;font-size:.73rem;color:#1565c0">
                  <strong>Segmentación Andromeda:</strong> {{ creative.result.segmentacion }}
                </div>
                <b-button variant="outline-secondary" size="sm" class="mt-2"
                  @click="copyText(`${creative.result.titular}\n\n${creative.result.texto_principal}\n\nCTA: ${creative.result.cta}`)">
                  📋 Copiar copy completo
                </b-button>
              </b-col>

              <b-col md="6">
                <div class="creative-field mb-2">
                  <div class="creative-field-label">
                    PROMPT DE IMAGEN
                    <span style="color:#ef4444;font-weight:400;margin-left:4px">← Revisa antes de gastar $0.08</span>
                  </div>
                  <b-form-textarea v-model="creative.imagePrompt" rows="7" size="sm" />
                  <div style="font-size:.68rem;color:#6b7094;margin-top:4px">
                    Colores GoGuau:
                    <span style="display:inline-block;width:10px;height:10px;background:#FFCC00;border:1px solid #999;border-radius:2px;vertical-align:middle;margin:0 2px"></span>#FFCC00
                    <span style="display:inline-block;width:10px;height:10px;background:#1A2E5A;border:1px solid #999;border-radius:2px;vertical-align:middle;margin:0 2px 0 6px"></span>#1A2E5A
                    <span style="display:inline-block;width:10px;height:10px;background:#5046AF;border:1px solid #999;border-radius:2px;vertical-align:middle;margin:0 2px 0 6px"></span>#5046AF
                  </div>
                </div>
              </b-col>
            </b-row>

            <hr class="my-3">
            <div class="step-header mb-2">
              <span class="step-badge step-badge-3">3</span>
              <strong>Generar imagen con Gemini</strong>
              <b-badge variant="warning" class="ml-2" style="font-size:.68rem;font-weight:700">$0.08 USD por imagen</b-badge>
            </div>

            <b-button variant="success" :disabled="creative.generating || !inferenceKey || !creative.imagePrompt" @click="generateCreative" class="mb-3">
              ✦ Generar Imagen con Gemini
            </b-button>

            <div v-if="creative.generating" class="text-center py-4">
              <b-spinner variant="success" style="width:3rem;height:3rem" />
              <div class="mt-2 font-weight-bold" style="color:#22c55e">Generando imagen con Gemini…</div>
              <div style="font-size:.78rem;color:#6b7094">20–40 segundos. No cierres el modal.</div>
            </div>

            <div v-if="creative.imageUrl && !creative.generating" class="text-center mt-2">
              <img :src="creative.imageUrl" alt="Creativo generado" style="max-width:100%;max-height:480px;border-radius:12px;border:2px solid #dee2e6" />
              <div class="mt-2" style="display:flex;gap:8px;justify-content:center;flex-wrap:wrap">
                <a :href="creative.imageUrl" target="_blank">
                  <b-button variant="outline-secondary" size="sm">⬇ Descargar imagen</b-button>
                </a>
                <b-button variant="warning" size="sm" @click="saveToTimeline">
                  📅 Guardar en Timeline
                </b-button>
              </div>
            </div>

            <!-- Guardar sin imagen (solo copy listo) -->
            <div v-if="creative.copyReady && !creative.imageUrl && !creative.generating" class="mt-3 text-center">
              <b-button variant="outline-warning" size="sm" @click="saveToTimeline">
                📅 Guardar en Timeline (solo copy)
              </b-button>
            </div>
          </div>

        </div>
      </b-modal>

    </div>
  </div>
</template>

<script>
import Loading from 'vue-loading-overlay'
import 'vue-loading-overlay/dist/vue-loading.css'
import { CChartLine, CChartBar, CChartDoughnut } from '@coreui/vue-chartjs'
import MetaTimeline from './MetaTimeline.vue'

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#84cc16']

const PRINCIPLES = [
  {
    name: 'Make it about them — Hazlo sobre ellos',
    description: 'El cliente no compra el servicio — compra lo que ese servicio hace por su mascota y su propia vida. Menciona el beneficio emocional ANTES de cualquier característica.',
    example: '"Mientras tú trabajas tranquilo, Rex quema energía y llega a casa feliz 🐕‍🦺 — con GPS en tiempo real para tu paz mental 📍"',
  },
  {
    name: 'Make it an event — Conviértelo en un evento',
    description: 'Urgencia real y prueba social: otros ya agendaron, los cupos se llenan, actúa ahora. FOMO activado.',
    example: '"Los cupos del horario de mañana en tu zona se completan rápido. ¿Apartamos el de tu peludito ahora? 🙌"',
  },
  {
    name: 'Powerful demonstration — Demostración con impacto',
    description: 'Muestra el servicio en acción antes de dar el precio. Ver elimina el riesgo percibido y acelera la confianza.',
    example: '"Mira cómo funciona en la práctica 👇 GPS en tiempo real, paseadores verificados, 1.2 km garantizados."',
  },
  {
    name: 'Show unquestionable proof — Prueba irrefutable',
    description: 'Cifras reales, autoridad institucional, +40.000 servicios. El cerebro necesita justificación racional para validar la decisión emocional.',
    example: '"GoGuau: empresa registrada SIC, +40.000 servicios desde 2022, mejor emprendimiento del año 🏅. Paseadores con verificación de antecedentes."',
  },
  {
    name: 'Change a daily behavior — Cambia un comportamiento diario',
    description: 'El servicio debe volverse parte de la rutina. Enmarcar como "la rutina de tu mascota", no como una compra puntual.',
    example: '"Los lunes, miércoles y viernes son los días de tu peludito 📅 — desde la segunda semana ya es su rutina favorita."',
  },
  {
    name: 'Sell the dream — Vende el sueño',
    description: 'Proyectar el resultado emocional ideal. "Imagina llegar a casa y encontrar una mascota feliz y calmada." Las decisiones son emocionales.',
    example: '"Imagina llegar a casa después de un largo día y que tu peludito ya esté calmado, feliz y listo para estar contigo 🌟"',
  },
  {
    name: 'Help them rebel — Ayúdalos a rebelarse',
    description: 'Posicionar al dueño como el que elige diferente. El "enemigo" es el statu quo: mascotas solas 8 horas, guarderías con jaulas, sin rutina.',
    example: '"Muchos dueños siguen dejando a su peludito encerrado 8 horas sin moverse 😕 Tú ya estás dando el paso diferente 🐾💚"',
  },
]

const ANDROMEDA_ANGLES = [
  {
    id: 'dolor',
    name: 'Directo al Dolor',
    icon: '🔴',
    description: 'Ataca la frustración inmediata del dueño. Hook mostrando el problema en los primeros 2 segundos.',
    format: 'Video UGC corto con gancho fuerte',
    goguau: '¿Tu perro pasa 8 horas solo en casa? Eso tiene solución.',
  },
  {
    id: 'social',
    name: 'Prueba Social',
    icon: '⭐',
    description: 'Testimonios reales, cifras de clientes. Elimina el riesgo percibido antes de que pregunten el precio.',
    format: 'Carrusel de reseñas o video demo',
    goguau: '+1.200 dueños en Colombia ya confían en GoGuau.',
  },
  {
    id: 'educativo',
    name: 'Educativo',
    icon: '🧠',
    description: 'Enseña algo que el usuario no sabía que necesitaba. Genera autoridad y confianza de marca.',
    format: 'Video explainer o infografía animada',
    goguau: '3 señales de que tu perro necesita más ejercicio del que crees.',
  },
  {
    id: 'aspiracional',
    name: 'Aspiracional',
    icon: '✨',
    description: 'Muestra el resultado final: mascota feliz y calmada, dueño tranquilo. Decisión emocional primero.',
    format: 'Imagen estática de alta calidad o Reel estético',
    goguau: 'Llega a casa y encuentra a tu peludito calmado y feliz.',
  },
]

const CPR_GREEN = 2500
const CPR_YELLOW = 4000
const CPR_BENCHMARK = 3518
const CACHE_TTL = 5 * 60 * 1000
const API_VERSION = 'v18.0'

export default {
  name: 'MetaAdsDashboard',
  components: { Loading, CChartLine, CChartBar, CChartDoughnut, MetaTimeline },

  data () {
    const today = new Date()
    const pad = n => String(n).padStart(2, '0')
    const fmtD = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
    const ago30 = new Date(today); ago30.setDate(ago30.getDate() - 29)

    return {
      COLORS,
      PRINCIPLES,
      ANDROMEDA_ANGLES,
      hasAccess: false,
      loading: false,
      errors: [],
      period: 'last_30d',
      dateFrom: fmtD(ago30),
      dateTo: fmtD(today),
      lastRefresh: null,
      campaigns: [],
      insights: [],
      dailyInsights: [],
      placementInsights: [],
      monthlyInsights: [],
      adInsights: [],
      expandedCampaigns: {},
      avgTicket: 55000,
      creative: {
        show: false,
        ad: null,
        campaign: null,
        angleIdx: 0,
        principleIdx: 0,
        comment: '',
        claudeGenerating: false,
        generating: false,
        copyReady: false,
        error: null,
        result: null,
        imagePrompt: '',
        imageUrl: null,
        rawText: null,
      },
      sortKey: 'spend',
      sortAsc: false,
      periodTabs: [
        { value: 'today', label: 'Hoy' },
        { value: 'last_7d', label: '7 días' },
        { value: 'last_30d', label: '30 días' },
        { value: 'this_month', label: 'Este mes' },
        { value: 'last_month', label: 'Mes anterior' },
        { value: 'ytd', label: 'Año' },
        { value: 'custom', label: 'Personalizado' },
      ],
    }
  },

  computed: {
    token () {
      return process.env.VUE_APP_META_TOKEN || ''
    },
    accountId () {
      return process.env.VUE_APP_META_ACCOUNT_ID || 'act_632586176429918'
    },

    kpiCards () {
      const ins = this.insights
      const totalSpend = ins.reduce((s, r) => s + parseFloat(r.spend || 0), 0)
      const totalConvs = ins.reduce((s, r) => s + this.getConvs(r.actions), 0)
      const totalReach = ins.reduce((s, r) => s + parseInt(r.reach || 0), 0)
      const totalImpr  = ins.reduce((s, r) => s + parseInt(r.impressions || 0), 0)
      const avgFreq    = totalReach > 0 ? (totalImpr / totalReach).toFixed(1) : 0
      const avgCpr     = totalConvs > 0 ? totalSpend / totalConvs : 0
      const active     = this.campaigns.filter(c => c.effective_status === 'ACTIVE').length
      const paused     = this.campaigns.filter(c => c.effective_status === 'PAUSED').length

      return [
        { label: 'Gasto total', value: this.copFmt(totalSpend), sub: `${this.copFmt(avgCpr)}/conv`, color: null },
        { label: 'CPR promedio', value: this.copFmt(avgCpr), sub: 'Costo por conversación', color: this.cprColor(avgCpr) },
        { label: 'Conversaciones', value: this.numFmt(totalConvs), sub: totalConvs > 0 ? `${this.copFmt(totalSpend / totalConvs)} por conv` : '', color: '#22c55e' },
        { label: 'Alcance', value: this.numFmt(totalReach), sub: 'Personas únicas', color: null },
        { label: 'Impresiones', value: this.numFmt(totalImpr), sub: `Frecuencia: ${avgFreq}x`, color: null },
        { label: 'Campañas activas', value: String(active), sub: `${paused} pausada${paused !== 1 ? 's' : ''}`, color: null },
      ]
    },

    tableRows () {
      const rows = this.insights.map(r => {
        const convs = this.getConvs(r.actions)
        const spend = parseFloat(r.spend || 0)
        const cpr   = convs > 0 ? Math.round(spend / convs) : 0
        const camp  = this.campaigns.find(c => c.id === r.campaign_id) || {}
        const n     = r.campaign_name.toUpperCase()
        const city  = ['Armenia', 'Ibagué', 'Pereira', 'Popayán'].find(c => r.campaign_name.toLowerCase().includes(c.toLowerCase())) || ''
        const type  = n.includes('PPL') ? 'PPL' : (n.includes('CP') || n.includes('CONVOCATORIA')) ? 'Conv. CP' : n.includes('RETARGETING') ? 'Retargeting' : ''
        return {
          id: r.campaign_id,
          name: r.campaign_name,
          status: camp.effective_status || 'UNKNOWN',
          spend, convs, cpr,
          reach: parseInt(r.reach || 0),
          hint: [city, type].filter(Boolean).join(' · '),
          rec: this.getRecommendation({ name: r.campaign_name, cpr, status: camp.effective_status || 'UNKNOWN', spend, convs }),
          delta: null,
        }
      })

      const totalSpend = rows.reduce((s, r) => s + r.spend, 0)
      const totalConvs = rows.reduce((s, r) => s + r.convs, 0)
      const avgCpr = totalConvs > 0 ? totalSpend / totalConvs : 0

      rows.forEach(row => {
        row.delta = avgCpr > 0 && row.cpr > 0
          ? Math.round(((row.cpr - avgCpr) / avgCpr) * 100)
          : null
      })

      const key = this.sortKey
      rows.sort((a, b) => {
        const va = a[key] ?? 0, vb = b[key] ?? 0
        if (typeof va === 'string') return this.sortAsc ? va.localeCompare(vb) : vb.localeCompare(va)
        return this.sortAsc ? va - vb : vb - va
      })
      return rows
    },

    alerts () {
      const rows = this.tableRows
      const alerts = []
      const totalSpend = rows.reduce((s, r) => s + r.spend, 0)
      const totalConvs = rows.reduce((s, r) => s + r.convs, 0)
      const avgCpr = totalConvs > 0 ? totalSpend / totalConvs : 0

      rows.filter(r => r.cpr > CPR_YELLOW && r.status === 'ACTIVE' && r.spend > 50000)
        .forEach(r => {
          const waste = r.spend - (r.convs * CPR_BENCHMARK)
          alerts.push({
            type: 'critical', icon: '🔴',
            title: `${r.name.slice(0, 45)} — CPR ${this.copFmt(r.cpr)}`,
            body: `Esta campaña está ${Math.round(((r.cpr - CPR_BENCHMARK) / CPR_BENCHMARK) * 100)}% por encima del benchmark ($3,518). Sobrecosto estimado: ${this.copFmt(waste)}.`,
            action: r.name.toUpperCase().includes('PPL')
              ? 'Acción: Pausar PPL y activar Convocatoria CP'
              : 'Acción: Revisar segmentación y creativos',
          })
        })

      rows.filter(r => {
        const n = r.name.toUpperCase()
        return (n.includes('BORRAR') || n.includes('PRUEBA') || n.includes('ZIP')) && r.spend > 0
      }).forEach(r => alerts.push({
        type: 'critical', icon: '🗑️',
        title: `Campaña borrador activa: ${r.name.slice(0, 45)}`,
        body: `Gasto detectado en campaña que debería eliminarse: ${this.copFmt(r.spend)}.`,
        action: 'Acción: Eliminar del Ads Manager inmediatamente',
      }))

      rows.filter(r => r.convs === 0 && r.spend > 30000 && r.status === 'ACTIVE')
        .forEach(r => alerts.push({
          type: 'warning', icon: '⚠️',
          title: `Sin conversiones: ${r.name.slice(0, 45)}`,
          body: `${this.copFmt(r.spend)} gastados con 0 conversaciones. Puede ser problema de tracking o configuración.`,
          action: 'Acción: Verificar evento de conversión y pixel de Meta',
        }))

      rows.filter(r => r.status === 'PAUSED' && (r.name.toUpperCase().includes('CP') || r.name.toUpperCase().includes('CONVOCATORIA')) && r.cpr > 0 && r.cpr < CPR_YELLOW)
        .forEach(r => alerts.push({
          type: 'opportunity', icon: '💡',
          title: `Oportunidad: ${r.name.slice(0, 45)} pausada`,
          body: `CPR histórico ${this.copFmt(r.cpr)} — mejor que el promedio de campañas activas (${this.copFmt(avgCpr)}).`,
          action: 'Acción: Activar con $38,000 COP/día y monitorear CPR en 48h',
        }))

      this.insights.forEach(r => {
        const freq = parseFloat(r.frequency || 0)
        if (freq > 3.5 && parseFloat(r.spend || 0) > 50000) {
          alerts.push({
            type: 'warning', icon: '🔄',
            title: `Frecuencia alta: ${(r.campaign_name || '').slice(0, 40)}`,
            body: `Frecuencia de ${freq.toFixed(1)}x — audiencia saturada, CPR probablemente empeore.`,
            action: 'Acción: Ampliar audiencia o rotar creativos',
          })
        }
      })

      if (alerts.length === 0) {
        alerts.push({
          type: 'opportunity', icon: '✅',
          title: 'Portafolio en buen estado',
          body: `CPR promedio: ${this.copFmt(avgCpr)}. Sin alertas críticas.`,
          action: avgCpr > 0 ? `Benchmark anual: $3,518/conv` : '',
        })
      }
      return alerts
    },

    dailySpendChart () {
      const daily = this.dailyInsights
      if (!daily.length) return { datasets: [], labels: [] }
      const dates = [...new Set(daily.map(r => r.date_start))].sort()
      const names = [...new Set(daily.map(r => r.campaign_name))]
      const datasets = names.map((name, i) => ({
        label: name.length > 30 ? name.slice(0, 30) + '…' : name,
        data: dates.map(d => {
          const row = daily.find(r => r.campaign_name === name && r.date_start === d)
          return row ? parseFloat(row.spend) : 0
        }),
        borderColor: COLORS[i % COLORS.length],
        backgroundColor: COLORS[i % COLORS.length] + '20',
        borderWidth: 2,
        pointRadius: dates.length > 14 ? 0 : 3,
        tension: 0.3,
        fill: false,
      }))
      return { datasets, labels: dates.map(this.fmtDate) }
    },

    dailyCprChart () {
      const daily = this.dailyInsights
      if (!daily.length) return { datasets: [], labels: [] }
      const dates = [...new Set(daily.map(r => r.date_start))].sort()
      const cprByDay = dates.map(d => {
        const rows = daily.filter(r => r.date_start === d)
        const spend = rows.reduce((s, r) => s + parseFloat(r.spend || 0), 0)
        const convs = rows.reduce((s, r) => s + this.getConvs(r.actions), 0)
        return convs > 0 ? Math.round(spend / convs) : null
      })
      return {
        labels: dates.map(this.fmtDate),
        datasets: [
          {
            label: 'CPR diario (COP)',
            data: cprByDay,
            borderColor: '#f59e0b',
            backgroundColor: '#f59e0b20',
            borderWidth: 2,
            pointRadius: dates.length > 14 ? 0 : 3,
            tension: 0.3,
            spanGaps: true,
          },
          {
            label: `Benchmark ($${CPR_BENCHMARK.toLocaleString('es-CO')})`,
            data: dates.map(() => CPR_BENCHMARK),
            borderColor: '#ef444460',
            borderDash: [6, 4],
            borderWidth: 1.5,
            pointRadius: 0,
          },
        ],
      }
    },

    campaignConvsChart () {
      const rows = this.tableRows.filter(r => r.convs > 0).slice(0, 12)
      if (!rows.length) return { datasets: [], labels: [] }
      return {
        labels: rows.map(r => r.name.length > 25 ? r.name.slice(0, 25) + '…' : r.name),
        datasets: [{
          label: 'Conversaciones',
          data: rows.map(r => r.convs),
          backgroundColor: rows.map((_, i) => COLORS[i % COLORS.length] + 'cc'),
          borderRadius: 6,
        }],
      }
    },

    placementChart () {
      const platforms = this.platformsData
      if (!Object.keys(platforms).length) return { datasets: [], labels: [] }
      const labels = Object.keys(platforms).map(this.platformLabel)
      return {
        labels,
        datasets: [{
          data: Object.values(platforms).map(p => p.spend),
          backgroundColor: COLORS.slice(0, labels.length),
          borderWidth: 0,
          hoverOffset: 4,
        }],
      }
    },

    placementBreakdown () {
      const platforms = this.platformsData
      const total = Object.values(platforms).reduce((s, p) => s + p.spend, 0)
      return Object.entries(platforms).map(([p, v], i) => ({
        label: this.platformLabel(p),
        spend: v.spend,
        convs: v.convs,
        cpr: v.convs > 0 ? Math.round(v.spend / v.convs) : 0,
        pct: total > 0 ? ((v.spend / total) * 100).toFixed(1) : '0',
        color: COLORS[i],
      }))
    },

    inferenceKey () {
      return process.env.VUE_APP_INFERENCE_API_KEY || ''
    },
    calculatedKpis () {
      const ins = this.insights
      const totalSpend = ins.reduce((s, r) => s + parseFloat(r.spend || 0), 0)
      const totalConvs = ins.reduce((s, r) => s + this.getConvs(r.actions), 0)
      const ingresoEst = totalConvs * this.avgTicket
      const roas       = totalSpend > 0 ? (ingresoEst / totalSpend).toFixed(2) : '—'

      // Budget en riesgo: anuncios DETENER o PAUSAR (red/yellow cls)
      let budgetRiesgo = 0
      let bestAdCpr = 0
      this.adInsights.forEach(ad => {
        const convs = this.getConvs(ad.actions)
        const spend = parseFloat(ad.spend || 0)
        const cpr   = convs > 0 ? Math.round(spend / convs) : 0
        const rec   = this.getAdRecommendation({ name: ad.ad_name || '', spend, convs, cpr, freq: parseFloat(ad.frequency || 0), ctr: parseFloat(ad.ctr || 0) })
        if (rec.cls === 'red' || (rec.label === 'PAUSAR')) budgetRiesgo += spend
        if (cpr > 0 && cpr <= CPR_GREEN && (bestAdCpr === 0 || cpr < bestAdCpr)) bestAdCpr = cpr
      })
      const potencial = bestAdCpr > 0 ? Math.round(budgetRiesgo / bestAdCpr) : 0

      return [
        { label: 'Ingreso estimado', value: this.copFmt(ingresoEst), sub: `${totalConvs} conv × $${(this.avgTicket/1000).toFixed(0)}K ticket`, color: '#22c55e' },
        { label: 'ROAS estimado', value: roas !== '—' ? `${roas}×` : '—', sub: 'Retorno sobre inversión publicitaria', color: parseFloat(roas) >= 3 ? '#22c55e' : parseFloat(roas) >= 1.5 ? '#f59e0b' : '#ef4444' },
        { label: 'Budget en riesgo', value: this.copFmt(budgetRiesgo), sub: 'En anuncios DETENER · PAUSAR', color: budgetRiesgo > 0 ? '#ef4444' : '#22c55e' },
        { label: 'Conv. potenciales', value: potencial > 0 ? `+${potencial}` : '—', sub: 'Si reasignas el budget en riesgo', color: '#8b5cf6' },
      ]
    },

    adsByCampaign () {
      const map = {}
      this.adInsights.forEach(ad => {
        if (!map[ad.campaign_id]) map[ad.campaign_id] = []
        map[ad.campaign_id].push(ad)
      })
      return map
    },

    platformsData () {
      const platforms = {}
      this.placementInsights.forEach(r => {
        const p = r.publisher_platform || 'unknown'
        if (!platforms[p]) platforms[p] = { spend: 0, convs: 0 }
        platforms[p].spend += parseFloat(r.spend || 0)
        platforms[p].convs += this.getConvs(r.actions)
      })
      return platforms
    },

    monthlyBreakdown () {
      return this.monthlyInsights.map(r => {
        const convs = this.getConvs(r.actions)
        const spend = parseFloat(r.spend || 0)
        const cpr   = convs > 0 ? Math.round(spend / convs) : 0
        return { label: this.fmtMonth(r.date_start), spend, convs, cpr }
      })
    },

    monthlyChart () {
      if (!this.monthlyInsights.length) return { datasets: [], labels: [] }
      const labels = this.monthlyInsights.map(r => this.fmtMonth(r.date_start))
      return {
        labels,
        datasets: [
          {
            label: 'Gasto (COP)',
            data: this.monthlyInsights.map(r => parseFloat(r.spend || 0)),
            backgroundColor: '#3b82f680',
            borderRadius: 4,
            yAxisID: 'y',
          },
          {
            label: 'CPR (COP/conv)',
            data: this.monthlyInsights.map(r => {
              const convs = this.getConvs(r.actions)
              return convs > 0 ? Math.round(parseFloat(r.spend || 0) / convs) : 0
            }),
            type: 'line',
            borderColor: '#f59e0b',
            backgroundColor: 'transparent',
            borderWidth: 2,
            pointRadius: 3,
            yAxisID: 'y2',
          },
        ],
      }
    },
  },

  created () {
    const profile = JSON.parse(this.$store.getters.userProfile)
    if (profile.acceso && profile.acceso.id === 1) {
      this.hasAccess = true
      if (!this.token) {
        this.errors.push('VUE_APP_META_TOKEN no está configurado. Agrégalo al archivo .env.')
        return
      }
      this.loadData()
    }
  },

  methods: {
    // ── Formateo ─────────────────────────────────────────────
    copFmt (n) {
      if (!n || isNaN(n)) return '$0'
      const v = Math.round(Number(n))
      if (v >= 1000000) return `$${(v / 1000000).toFixed(1)}M`
      if (v >= 1000) return `$${(v / 1000).toFixed(0)}K`
      return `$${v.toLocaleString('es-CO')}`
    },
    numFmt (n) {
      if (!n || isNaN(n)) return '0'
      const v = Math.round(Number(n))
      if (v >= 1000000) return `${(v / 1000000).toFixed(1)}M`
      if (v >= 1000) return `${(v / 1000).toFixed(1)}K`
      return v.toLocaleString('es-CO')
    },
    fmtDate (s) {
      const d = new Date(s + 'T00:00:00')
      return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short' })
    },
    fmtMonth (s) {
      const d = new Date(s.slice(0, 7) + '-01T00:00:00')
      return d.toLocaleDateString('es-CO', { month: 'short', year: '2-digit' })
    },
    cprColor (cpr) {
      if (!cpr || cpr === 0) return '#6b7094'
      if (cpr <= CPR_GREEN) return '#22c55e'
      if (cpr <= CPR_YELLOW) return '#f59e0b'
      return '#ef4444'
    },
    recVariant (cls) {
      return { green: 'success', yellow: 'warning', red: 'danger', blue: 'primary', gray: 'secondary' }[cls] || 'secondary'
    },
    platformLabel (p) {
      return { facebook: 'Facebook', instagram: 'Instagram', messenger: 'Messenger', audience_network: 'Audience Net.' }[p] || p
    },

    // ── Conversiones ─────────────────────────────────────────
    getConvs (actions) {
      if (!actions) return 0
      const types = [
        'onsite_conversion.messaging_conversation_started_7d',
        'onsite_conversion.messaging_first_reply',
        'lead', 'contact', 'offsite_conversion.fb_pixel_lead',
      ]
      for (const t of types) {
        const a = actions.find(x => x.action_type === t)
        if (a && parseInt(a.value) > 0) return parseInt(a.value)
      }
      return actions
        .filter(x => x.action_type.includes('messaging') || x.action_type === 'lead')
        .reduce((s, x) => s + parseInt(x.value || 0), 0)
    },

    // ── Período ──────────────────────────────────────────────
    getPeriodDates () {
      const today = new Date()
      const pad = d => String(d).padStart(2, '0')
      const fmt = d => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
      const add = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x }
      const until = fmt(today)
      switch (this.period) {
        case 'today':      return { since: until, until }
        case 'last_7d':    return { since: fmt(add(today, -6)), until }
        case 'last_30d':   return { since: fmt(add(today, -29)), until }
        case 'this_month': return { since: `${today.getFullYear()}-${pad(today.getMonth() + 1)}-01`, until }
        case 'last_month': {
          const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1)
          const lme = new Date(today.getFullYear(), today.getMonth(), 0)
          return { since: fmt(lm), until: fmt(lme) }
        }
        case 'ytd':        return { since: `${today.getFullYear()}-01-01`, until }
        case 'custom':     return (this.dateFrom && this.dateTo) ? { since: this.dateFrom, until: this.dateTo } : { since: fmt(add(today, -29)), until }
        default:           return { since: fmt(add(today, -29)), until }
      }
    },

    // ── Cache ────────────────────────────────────────────────
    cacheGet (key) {
      try {
        const item = JSON.parse(localStorage.getItem(`mgd_${key}`))
        if (item && Date.now() - item.ts < CACHE_TTL) return item.data
      } catch (e) { /* ignorar parse errors */ }
      return null
    },
    cacheSet (key, data) {
      try {
        localStorage.setItem(`mgd_${key}`, JSON.stringify({ ts: Date.now(), data }))
      } catch (e) { /* ignorar cuota de storage */ }
    },

    // ── API ──────────────────────────────────────────────────
    async fetchAllPages (path, params) {
      const base = `https://graph.facebook.com/${API_VERSION}`
      const url = new URL(`${base}/${path}`)
      url.searchParams.set('access_token', this.token)
      Object.entries(params).forEach(([k, v]) => {
        url.searchParams.set(k, typeof v === 'object' ? JSON.stringify(v) : String(v))
      })
      let results = []
      let currentUrl = url.toString()
      while (currentUrl) {
        const res = await fetch(currentUrl)
        const data = await res.json()
        if (data.error) throw new Error(`Meta API: ${data.error.message} (code ${data.error.code})`)
        results = results.concat(data.data || [])
        currentUrl = data.paging?.next || null
      }
      return results
    },

    async fetchCampaigns (force) {
      const key = `campaigns_${this.accountId}`
      if (!force) { const c = this.cacheGet(key); if (c) return c }
      const data = await this.fetchAllPages(`${this.accountId}/campaigns`, {
        fields: 'id,name,status,effective_status,daily_budget,lifetime_budget,objective',
        limit: 100,
      })
      this.cacheSet(key, data)
      return data
    },

    async fetchInsights (since, until, breakdown, force) {
      const key = `insights_${this.accountId}_${since}_${until}_${breakdown || 'none'}`
      if (!force) { const c = this.cacheGet(key); if (c) return c }
      const params = {
        fields: 'campaign_id,campaign_name,spend,impressions,reach,actions,cpm,cpc,ctr,frequency',
        level: 'campaign',
        time_range: { since, until },
        limit: 100,
      }
      if (breakdown) params.breakdowns = breakdown
      const data = await this.fetchAllPages(`${this.accountId}/insights`, params)
      this.cacheSet(key, data)
      return data
    },

    async fetchDailyInsights (since, until, force) {
      const key = `daily_${this.accountId}_${since}_${until}`
      if (!force) { const c = this.cacheGet(key); if (c) return c }
      const data = await this.fetchAllPages(`${this.accountId}/insights`, {
        fields: 'campaign_name,spend,actions,date_start',
        level: 'campaign',
        time_range: { since, until },
        time_increment: 1,
        limit: 500,
      })
      this.cacheSet(key, data)
      return data
    },

    async fetchAdInsights (since, until, force) {
      const key = `ads_${this.accountId}_${since}_${until}`
      if (!force) { const c = this.cacheGet(key); if (c) return c }
      const data = await this.fetchAllPages(`${this.accountId}/insights`, {
        fields: 'ad_id,ad_name,campaign_id,campaign_name,spend,impressions,reach,actions,ctr,frequency,cpm',
        level: 'ad',
        time_range: { since, until },
        limit: 300,
      })
      this.cacheSet(key, data)
      return data
    },

    async fetchMonthlyInsights (force) {
      const key = `monthly_${this.accountId}`
      if (!force) { const c = this.cacheGet(key); if (c) return c }
      const today = new Date()
      const pad = n => String(n).padStart(2, '0')
      const since = `${today.getFullYear() - 1}-${pad(today.getMonth() + 2)}-01`
      const until = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`
      const data = await this.fetchAllPages(`${this.accountId}/insights`, {
        fields: 'spend,actions,impressions,reach,date_start',
        level: 'account',
        time_range: { since, until },
        time_increment: 'monthly',
        limit: 24,
      })
      this.cacheSet(key, data)
      return data
    },

    // ── Carga principal ──────────────────────────────────────
    async loadData (force = false) {
      this.loading = true
      this.errors = []
      const { since, until } = this.getPeriodDates()
      try {
        const [campaigns, insights, daily, placement, monthly, ads] = await Promise.all([
          this.fetchCampaigns(force),
          this.fetchInsights(since, until, null, force),
          this.fetchDailyInsights(since, until, force),
          this.fetchInsights(since, until, 'publisher_platform', force),
          this.fetchMonthlyInsights(force),
          this.fetchAdInsights(since, until, force),
        ])
        this.campaigns         = campaigns
        this.insights          = insights
        this.dailyInsights     = daily
        this.placementInsights = placement
        this.monthlyInsights   = monthly
        this.adInsights        = ads
        this.lastRefresh = new Date().toLocaleTimeString('es-CO')
      } catch (err) {
        this.errors.push(`Error conectando a Meta API: ${err.message}`)
      } finally {
        this.loading = false
      }
    },

    // ── Anuncios ─────────────────────────────────────────────
    toggleCampaign (id) {
      this.$set(this.expandedCampaigns, id, !this.expandedCampaigns[id])
    },

    adsForCampaign (campaignId) {
      const ads = (this.adsByCampaign[campaignId] || []).map(ad => {
        const convs = this.getConvs(ad.actions)
        const spend = parseFloat(ad.spend || 0)
        const cpr   = convs > 0 ? Math.round(spend / convs) : 0
        const freq  = parseFloat(ad.frequency || 0)
        const ctr   = parseFloat(ad.ctr || 0)
        return {
          adId: ad.ad_id,
          name: ad.ad_name,
          spend, convs, cpr, freq, ctr,
          rec: this.getAdRecommendation({ name: ad.ad_name, spend, convs, cpr, freq, ctr }),
        }
      })
      return ads.sort((a, b) => b.spend - a.spend)
    },

    getAdRecommendation ({ name, spend, convs, cpr, freq, ctr }) {
      const n = name.toUpperCase()

      // Test/draft ads
      if (n.includes('BORRAR') || n.includes('PRUEBA') || n.includes('DRAFT') || n.includes('ZIP')) {
        return { label: 'ELIMINAR', cls: 'red', why: `Anuncio de prueba activo consumiendo ${this.copFmt(spend)}. Pausar y archivar.` }
      }

      // Crítico: frecuencia muy alta = fatiga severa de audiencia
      if (freq >= 5 && spend > 20000) {
        return { label: 'DETENER', cls: 'red', why: `Frecuencia ${freq.toFixed(1)}x — cada usuario ha visto este anuncio ${freq.toFixed(0)} veces en promedio. La audiencia está agotada: CPR seguirá escalando. Reemplazar creativo urgente.` }
      }

      // Sin conversiones con gasto alto = problema estructural
      if (convs === 0 && spend > 80000) {
        return { label: 'DETENER', cls: 'red', why: `${this.copFmt(spend)} gastados sin ninguna conversación. Revisar evento de conversión, objetivo de campaña y relevancia del creativo.` }
      }

      // CPR destructor de presupuesto
      if (cpr > 12000) {
        return { label: 'DETENER', cls: 'red', why: `CPR de ${this.copFmt(cpr)} — casi 4× el benchmark ($3,518). Este anuncio destruye presupuesto. Pausar y reasignar el budget a los anuncios con CPR verde.` }
      }

      // Fatiga + CPR elevado = creativo quemado
      if (freq >= 3.5 && cpr > CPR_YELLOW) {
        return { label: 'PAUSAR', cls: 'yellow', why: `Frecuencia ${freq.toFixed(1)}x + CPR ${this.copFmt(cpr)} — señal clara de fatiga creativa. La audiencia ya lo ignoró. Duplicar el conjunto de anuncios con creativo fresco.` }
      }

      // CTR muy bajo = mala conexión creativo/audiencia
      if (ctr < 0.4 && spend > 40000 && convs === 0) {
        return { label: 'REVISAR', cls: 'yellow', why: `CTR ${ctr.toFixed(2)}% — la audiencia no hace clic. El copy o la imagen no están conectando. Probar titular de pregunta o imagen con persona (no mascota sola).` }
      }

      // Sin conversiones con gasto moderado
      if (convs === 0 && spend > 25000) {
        return { label: 'REVISAR', cls: 'yellow', why: `${this.copFmt(spend)} sin conversaciones. Puede ser tracking roto o audiencia fría. Verificar pixel y considerar segmentación por intereses más específicos.` }
      }

      // Ganador absoluto: escalar ya
      if (cpr > 0 && cpr <= CPR_GREEN && ctr >= 1.5 && freq < 3) {
        return { label: 'ESCALAR', cls: 'green', why: `CPR ${this.copFmt(cpr)} + CTR ${ctr.toFixed(2)}% + frecuencia saludable (${freq.toFixed(1)}x). Este es tu mejor anuncio del período. Aumentar presupuesto 25–40% y expandir audiencia similar.` }
      }

      // Buen CPR, escalar con precaución
      if (cpr > 0 && cpr <= CPR_GREEN && freq < 3.5) {
        return { label: 'ESCALAR', cls: 'green', why: `CPR ${this.copFmt(cpr)} por debajo del umbral verde ($2,500). Aumentar budget 15–20% y monitorear frecuencia (ahora ${freq.toFixed(1)}x).` }
      }

      // CPR verde pero frecuencia subiendo — extender audiencia
      if (cpr > 0 && cpr <= CPR_GREEN) {
        return { label: 'AMPLIAR', cls: 'green', why: `CPR bueno (${this.copFmt(cpr)}) pero frecuencia ${freq.toFixed(1)}x creciendo. Mantener budget; ampliar audiencia (lookalike al 3–4%) para enfriar frecuencia sin perder eficiencia.` }
      }

      // CPR aceptable, mantener y vigilar
      if (cpr > 0 && cpr <= CPR_YELLOW && freq < 3) {
        return { label: 'MANTENER', cls: 'blue', why: `CPR ${this.copFmt(cpr)} en rango amarillo, frecuencia ${freq.toFixed(1)}x — tolerable. Monitorear semanalmente; si frecuencia supera 3.5x, rotar creativo.` }
      }

      // Activo pero CPR sobre umbral
      if (cpr > CPR_YELLOW) {
        return { label: 'OPTIMIZAR', cls: 'yellow', why: `CPR ${this.copFmt(cpr)} sobre el umbral ($4,000). Probar variante de copy con urgencia o prueba social antes de pausar.` }
      }

      return { label: 'MONITOREAR', cls: 'gray', why: 'Datos insuficientes para el período. Esperar más gasto para evaluar.' }
    },

    // ── Controles ────────────────────────────────────────────
    setPeriod (p) {
      this.period = p
      if (p !== 'custom') this.loadData(true)
    },
    sortTable (key) {
      if (this.sortKey === key) this.sortAsc = !this.sortAsc
      else { this.sortKey = key; this.sortAsc = false }
    },

    // ── Recomendaciones ──────────────────────────────────────
    getRecommendation ({ name, cpr, status, spend }) {
      const n = name.toUpperCase()
      const isCP    = n.includes('CP') || n.includes('CONVOCATORIA')
      const isPPL   = n.includes('PPL')
      const isBorrar = n.includes('BORRAR') || n.includes('PRUEBA') || n.includes('ZIP') || n.includes('DRAFT')
      if (isBorrar) return { label: 'ELIMINAR', cls: 'red', reason: 'Campaña de prueba — consume presupuesto sin valor' }
      if (status === 'PAUSED' && isCP && spend === 0) return { label: 'ACTIVAR', cls: 'green', reason: 'CP pausada — historial de bajo CPR' }
      if (cpr === 0 && spend > 0) return { label: 'REVISAR', cls: 'yellow', reason: 'Gasto sin conversiones registradas' }
      if (cpr > 10000) return { label: 'APAGAR', cls: 'red', reason: `CPR crítico: ${this.copFmt(cpr)}/conv` }
      if (cpr > CPR_YELLOW && isPPL) return { label: 'MIGRAR CP', cls: 'yellow', reason: `PPL cara — evaluar Convocatoria CP` }
      if (cpr > CPR_YELLOW) return { label: 'OPTIMIZAR', cls: 'yellow', reason: `CPR ${this.copFmt(cpr)} sobre umbral` }
      if (cpr > 0 && cpr <= CPR_GREEN && status === 'ACTIVE') return { label: 'ESCALAR', cls: 'green', reason: `CPR excelente: ${this.copFmt(cpr)}/conv` }
      if (cpr > 0 && cpr <= CPR_GREEN && status === 'PAUSED') return { label: 'REACTIVAR', cls: 'green', reason: 'Buen CPR histórico' }
      if (status === 'ACTIVE') return { label: 'MANTENER', cls: 'blue', reason: 'Rendimiento dentro del rango' }
      return { label: 'INACTIVA', cls: 'gray', reason: '' }
    },

    // ── Generador de Creativos ───────────────────────────────
    openCreativeModal (ad, campaign) {
      this.creative = {
        show: true,
        ad,
        campaign,
        angleIdx: 0,
        principleIdx: 0,
        comment: '',
        claudeGenerating: false,
        generating: false,
        copyReady: false,
        error: null,
        result: null,
        imagePrompt: '',
        imageUrl: null,
        rawText: null,
      }
    },

    buildCopyPrompt () {
      const ad    = this.creative.ad
      const camp  = this.creative.campaign
      const angle = this.ANDROMEDA_ANGLES[this.creative.angleIdx]
      const p     = this.PRINCIPLES[this.creative.principleIdx]
      const city  = ['Armenia', 'Ibagué', 'Pereira', 'Popayán'].find(c => (camp.name || '').toLowerCase().includes(c.toLowerCase())) || 'Colombia'
      const svc   = (camp.name || '').toUpperCase().includes('PPL') ? 'Paseo de perros' : (camp.name || '').toUpperCase().includes('GUARD') ? 'Guardería' : 'Servicios para mascotas'

      return `Eres un experto en marketing digital para servicios de mascotas en Colombia con 10+ años de experiencia y conocimiento profundo de Andromeda (el motor de IA de Meta Ads 2026).

CONTEXTO:
- Empresa: GoGuau — app colombiana de paseos, guardería y adiestramiento para mascotas
- Ciudad objetivo: ${city} | Servicio: ${svc}
- Empresa legalmente constituida, +40.000 servicios ejecutados, paseadores verificados con antecedentes
- Colores oficiales de marca: Amarillo #FFCC00, Azul Navy #1A2E5A, Azul Claro #87C8E8, Púrpura #5046AF
- Logo: bulldog francés animado sonriendo sobre un hueso amarillo, estilo cartoon amigable, tagline "Tu mejor amigo en manos amigas"

ANUNCIO DE REFERENCIA (mejor rendimiento del período):
- Nombre: "${ad.name}"
- CPR: ${this.copFmt(ad.cpr)}/conversación | CTR: ${ad.ctr.toFixed(2)}% | Conv: ${ad.convs}

ÁNGULO ANDROMEDA: "${angle.name}" ${angle.icon}
${angle.description}
Formato visual: ${angle.format}
Ejemplo GoGuau: "${angle.goguau}"

PRINCIPIO PSICOLÓGICO: "${p.name}"
${p.description}
${this.creative.comment ? `\nCOMENTARIO DEL GESTOR:\n${this.creative.comment}` : ''}

REGLAS ANDROMEDA 2026:
1. El beneficio emocional DEBE estar en las primeras 3 palabras del titular
2. Español colombiano natural, nunca corporativo
3. Máximo 1 emoji por sección
4. Usa "tu peludito" o "tu mascota", nunca nombre específico

Responde ÚNICAMENTE con este JSON (sin markdown, sin texto fuera del JSON):
{
  "titular": "máx 40 chars — beneficio en las primeras 3 palabras",
  "texto_principal": "máx 125 chars — emoción primero, servicio después",
  "cta": "máx 20 chars — acción clara",
  "imagen_sugerida": "descripción DETALLADA para el generador de imágenes: tipo de escena según ${angle.format}, descripción de persona si aplica, raza y estado de la mascota, ambiente urbano de ${city} Colombia, colores GoGuau prominentes (#FFCC00 amarillo #1A2E5A navy), logo GoGuau visible esquina inferior derecha, iluminación cálida, plano de cámara",
  "segmentacion": "audiencia implícita que Andromeda detectará del copy: comportamientos, estado de vida, intereses inferidos para ${city}",
  "por_que": "1 oración: por qué este ángulo + principio funcionará con Andromeda 2026"
}`
    },

    async generateCopy () {
      this.creative.claudeGenerating = true
      this.creative.error = null
      this.creative.copyReady = false
      this.creative.result = null
      this.creative.imagePrompt = ''
      this.creative.imageUrl = null

      try {
        const generateClaudeCopy = this.$firebase.functions().httpsCallable('generateClaudeCopy')
        const response = await generateClaudeCopy({prompt: this.buildCopyPrompt()})
        const claudeData = response.data?.data

        const text = claudeData?.content?.[0]?.text || ''
        const jsonMatch = text.match(/\{[\s\S]*\}/)
        if (!jsonMatch) throw new Error('Claude no devolvió un JSON válido. Intenta de nuevo.')

        const parsed = JSON.parse(jsonMatch[0])
        this.creative.result = {
          titular: parsed.titular || '',
          texto_principal: parsed.texto_principal || '',
          cta: parsed.cta || '',
          segmentacion: parsed.segmentacion || '',
          por_que: parsed.por_que || '',
        }
        this.creative.imagePrompt = parsed.imagen_sugerida || ''
        this.creative.copyReady = true
      } catch (err) {
        this.creative.error = `Error generando copy: ${err.message}`
      } finally {
        this.creative.claudeGenerating = false
      }
    },

    async generateCreative () {
      if (!this.inferenceKey) {
        this.creative.error = 'VUE_APP_INFERENCE_API_KEY no está configurado en .env'
        return
      }
      if (!this.creative.imagePrompt) {
        this.creative.error = 'El prompt de imagen está vacío. Genera el copy primero (Paso 1).'
        return
      }
      this.creative.generating = true
      this.creative.error = null
      this.creative.imageUrl = null

      try {
        const res = await fetch('https://api.inference.sh/apps/run', {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${this.inferenceKey}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            app: 'google/gemini-3-1-flash-image-preview@49k9nftt',
            input: {
              prompt: this.creative.imagePrompt,
              num_images: 1,
              aspect_ratio: '4:5',
              resolution: '1K',
              output_format: 'jpeg',
              safety_tolerance: 'BLOCK_NONE',
              retry_count: 2,
            },
          }),
        })

        const initial = await res.json()
        console.log('[inference.sh] POST response:', JSON.stringify(initial))
        if (!res.ok) throw new Error(initial.error || initial.message || `HTTP ${res.status}`)

        const taskData0 = initial.data || initial
        const taskId  = taskData0.id
        if (!taskId) throw new Error('La API no devolvió un ID de tarea')

        // 2. Polling: GET /tasks/{id}/status (liviano) → cuando completa, GET /tasks/{id}
        const statusUrl = `https://api.inference.sh/tasks/${taskId}/status`
        const fullUrl   = `https://api.inference.sh/tasks/${taskId}`
        const authHdr   = { 'Authorization': `Bearer ${this.inferenceKey}` }
        const deadline  = Date.now() + 120000
        let output = null
        let lastStatus = null

        while (Date.now() < deadline) {
          await new Promise(r => setTimeout(r, 3000))

          const stRes  = await fetch(statusUrl, { headers: authHdr })
          const stData = await stRes.json()
          const currentStatus = (stData.data || stData).status || (stData.data || stData).status_text
          console.log('[inference.sh] status:', currentStatus)

          if (currentStatus !== lastStatus) {
            lastStatus = currentStatus
            // Fetch completo cuando el status cambia
            const fullRes  = await fetch(fullUrl, { headers: authHdr })
            const fullData = await fullRes.json()
            const taskFull = fullData.data || fullData
            console.log('[inference.sh] full task:', JSON.stringify(taskFull).slice(0, 400))

            if (taskFull.output !== null && taskFull.output !== undefined) {
              output = taskFull.output
              break
            }
            const st = taskFull.status_text || taskFull.status
            if (st === 'failed' || taskFull.status === -1) {
              throw new Error(`La tarea falló: ${taskFull.error || st}`)
            }
          }
        }

        if (!output) throw new Error('Tiempo de espera agotado (120 s). Intenta de nuevo.')

        console.log('[inference.sh] output:', JSON.stringify(output))

        // 3. Extraer texto — soporta description / text / content
        const textContent = output.description || output.text || output.content || output.response || ''

        // Limpiar bloques markdown y extraer JSON
        const cleaned = textContent.replace(/```json\s*/gi, '').replace(/```\s*/g, '')
        const jsonMatch = cleaned.match(/\{[\s\S]*\}/)
        if (jsonMatch) {
          try {
            const parsed = JSON.parse(jsonMatch[0])
            if (parsed && (parsed.titular || parsed.texto_principal || parsed.cta)) {
              this.creative.result = parsed
            }
          } catch { /* JSON malformado */ }
        }
        if (!this.creative.result && textContent) {
          this.creative.result = { titular: '', texto_principal: textContent.slice(0, 800), cta: '', imagen_sugerida: '', segmentacion: '', por_que: '' }
        }

        // 4. Imagen generada
        const images = output.images || output.image_urls || []
        if (images.length > 0) {
          const img = images[0]
          this.creative.imageUrl = typeof img === 'string' ? img : (img.url || img.data || null)
        }
      } catch (err) {
        console.error('[inference.sh] error:', err)
        this.creative.error = `Error generando creativo: ${err.message}`
      } finally {
        this.creative.generating = false
      }
    },

    async saveToTimeline () {
      const angle = this.ANDROMEDA_ANGLES[this.creative.angleIdx]
      const p     = this.PRINCIPLES[this.creative.principleIdx]
      const camp  = this.creative.campaign
      const city  = ['Armenia', 'Ibagué', 'Pereira', 'Popayán']
        .find(c => (camp.name || '').toLowerCase().includes(c.toLowerCase())) || 'Colombia'
      const svc   = (camp.name || '').toUpperCase().includes('PPL')
        ? 'Paseo de perros'
        : (camp.name || '').toUpperCase().includes('GUARD')
          ? 'Guardería'
          : 'Servicios para mascotas'

      const data = {
        status:           'borrador',
        city,
        service:          svc,
        angle:            angle.id,
        angle_name:       angle.name,
        principle:        p.name,
        copy:             this.creative.result ? { ...this.creative.result } : {},
        image_url:        this.creative.imageUrl || '',
        image_prompt:     this.creative.imagePrompt || '',
        ad_name_ref:      this.creative.ad.name,
        campaign_name_ref: camp.name,
      }

      try {
        const db  = this.$firebase.firestore()
        const now = new Date()
        await db.collection('meta_timeline').add({ ...data, created_at: now, updated_at: now })
        this.$bvToast.toast('Pieza guardada en el Timeline', {
          title: 'Timeline',
          variant: 'success',
          autoHideDelay: 3000,
          solid: true,
        })
      } catch (err) {
        this.$bvToast.toast(`Error guardando en Timeline: ${err.message}`, {
          title: 'Error',
          variant: 'danger',
          autoHideDelay: 4000,
        })
      }
    },

    copyText (text) {
      navigator.clipboard.writeText(text).catch(() => {
        const el = document.createElement('textarea')
        el.value = text
        document.body.appendChild(el)
        el.select()
        document.execCommand('copy')
        document.body.removeChild(el)
      })
    },

    // ── Opciones de Chart (Chart.js 2.x — xAxes/yAxes) ──────
    lineChartOptions (copFormat) {
      const copFmt = this.copFmt
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { labels: { fontColor: '#374151', fontSize: 11, boxWidth: 14, padding: 16 } },
        tooltips: {
          mode: 'index',
          intersect: false,
          callbacks: copFormat
            ? { label: (item, data) => ` ${data.datasets[item.datasetIndex].label}: ${copFmt(item.yLabel)}` }
            : {},
        },
        scales: {
          xAxes: [{
            ticks: { fontColor: '#6b7094', fontSize: 10, maxRotation: 45, autoSkip: true, maxTicksLimit: 14 },
            gridLines: { color: 'rgba(0,0,0,.07)', zeroLineColor: 'rgba(0,0,0,.15)' },
          }],
          yAxes: [{
            ticks: { fontColor: '#6b7094', fontSize: 10, callback: v => copFormat ? copFmt(v) : v },
            gridLines: { color: 'rgba(0,0,0,.07)' },
          }],
        },
      }
    },
    barHorizOptions () {
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        tooltips: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{
            ticks: { fontColor: '#6b7094', fontSize: 9, maxRotation: 40, minRotation: 20, autoSkip: false },
            gridLines: { color: 'rgba(0,0,0,.07)' },
          }],
          yAxes: [{
            ticks: { fontColor: '#6b7094', fontSize: 10, beginAtZero: true },
            gridLines: { display: false },
          }],
        },
      }
    },
    doughnutOptions () {
      const copFmt = this.copFmt
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { position: 'bottom', labels: { fontColor: '#374151', fontSize: 11, padding: 14, boxWidth: 12 } },
        tooltips: { callbacks: { label: (item, data) => ` ${data.labels[item.index]}: ${copFmt(data.datasets[0].data[item.index])}` } },
      }
    },
    monthlyChartOptions () {
      const copFmt = this.copFmt
      return {
        responsive: true,
        maintainAspectRatio: false,
        legend: { labels: { fontColor: '#374151', fontSize: 11, boxWidth: 14, padding: 16 } },
        tooltips: { mode: 'index', intersect: false },
        scales: {
          xAxes: [{ ticks: { fontColor: '#6b7094', fontSize: 10 }, gridLines: { color: 'rgba(0,0,0,.07)' } }],
          yAxes: [
            { id: 'y',  position: 'left',  ticks: { fontColor: '#6b7094', fontSize: 10, callback: v => copFmt(v) }, gridLines: { color: 'rgba(0,0,0,.07)' } },
            { id: 'y2', position: 'right', ticks: { fontColor: '#6b7094', fontSize: 10, callback: v => copFmt(v) }, gridLines: { display: false } },
          ],
        },
      }
    },
  },
}
</script>

<style scoped>
.section-title {
  font-size: .78rem;
  font-weight: 700;
  color: #6b7094;
  text-transform: uppercase;
  letter-spacing: .1em;
  margin: 20px 0 10px;
}
.alert-item.critical    { background: rgba(239,68,68,.06); border: 1px solid rgba(239,68,68,.2); }
.alert-item.warning     { background: rgba(245,158,11,.06); border: 1px solid rgba(245,158,11,.2); }
.alert-item.opportunity { background: rgba(34,197,94,.06);  border: 1px solid rgba(34,197,94,.2); }
.alert-action-critical    { color: #ef4444; }
.alert-action-warning     { color: #f59e0b; }
.alert-action-opportunity { color: #22c55e; }

/* ── Header responsive ─────────────────────────────── */
.meta-header {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.meta-header-title {
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
}
.meta-refresh-label {
  font-size: .78rem;
  color: #6b7094;
}
.meta-period-row {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}
.meta-tabs-scroll {
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  flex: 1;
  min-width: 0;
}
.meta-tabs-scroll::-webkit-scrollbar { display: none; }
.meta-refresh-btn {
  flex-shrink: 0;
}
.meta-custom-range {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px;
}

/* ── Modal generador ──────────────────────────────── */
.creative-field { border-left: 3px solid #e9ecef; padding-left: 10px; }
.creative-field-label { font-size: .65rem; font-weight: 700; text-transform: uppercase; letter-spacing: .07em; color: #6b7094; margin-bottom: 3px; }

/* Pasos */
.step-block { border: 1px solid #e9ecef; border-radius: 8px; padding: 16px; }
.step-header { display: flex; align-items: center; gap: 8px; margin-bottom: 12px; }
.step-badge {
  display: inline-flex; align-items: center; justify-content: center;
  width: 24px; height: 24px; border-radius: 50%;
  background: #3b82f6; color: #fff; font-size: .75rem; font-weight: 700; flex-shrink: 0;
}
.step-badge-2 { background: #f59e0b; }
.step-badge-3 { background: #22c55e; }

/* Ángulos Andromeda */
.angle-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
.angle-card {
  border: 2px solid #e9ecef; border-radius: 8px; padding: 10px 8px;
  text-align: center; cursor: pointer; transition: all .15s;
}
.angle-card:hover { border-color: #3b82f6; background: #f0f4ff; }
.angle-card.selected { border-color: #3b82f6; background: #eff6ff; }
@media (max-width: 576px) { .angle-grid { grid-template-columns: repeat(2, 1fr); } }
.creative-field-value { font-size: .9rem; line-height: 1.5; }

/* ── Tabla campañas / anuncios ─────────────────────── */
.campaign-row:hover td { background: rgba(59,130,246,.06); }
.ad-row td { background: rgba(0,0,0,.02); }
.ad-row:hover td { background: rgba(59,130,246,.04); }
.ad-header-row td { background: rgba(0,0,0,.03); padding-top:6px; padding-bottom:6px; }
.ad-th {
  font-size: .68rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: .07em;
  color: #6b7094;
  cursor: default;
}
thead th { white-space: nowrap; }
</style>
